import { useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useModal } from '../../../components/common';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  CreateFamilyTreePayload,
  CreateTreeFormValues,
} from '../../../types/tree';
import { useCreateFamilyTree } from '../../../hooks/treeHooks';
import { ApiResponse } from '../../../types/api';
import { useAuth } from '../../../components/auth/AuthProvider';
import {
  getCharacterCountHelperText,
  hasReachedCharacterLimit,
  sanitiseNameInput,
  sanitiseTextInput,
} from '../../../utils';
import { textLimits } from '../../../core/constants/constants';

const accordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { mt: 0 },
} as const;

type CreateTreeProps = {
  redirectToStory?: boolean;
};

export default function CreateTree({
  redirectToStory = false,
}: CreateTreeProps) {
  const { currentUser } = useAuth();
  const { closeModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { control, handleSubmit } = useForm<CreateTreeFormValues>({
    defaultValues: {
      root_gender: 'male',
      father_is_alive: true,
      father_gender: 'male',
      mother_is_alive: true,
      mother_gender: 'female',
    },
  });
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { t } = useTranslation(['tree', 'common']);

  const mutation = useCreateFamilyTree();

  const makeDateHandler =
    (onChange: (val: string | null) => void) => (date: Date | null) =>
      onChange(
        date instanceof Date && !isNaN(date.valueOf())
          ? date.toISOString()
          : null,
      );

  const onSubmit = async (data: CreateTreeFormValues) => {
    if (!currentUser) {
      return;
    }

    const payload: CreateFamilyTreePayload = {
      name: sanitiseNameInput(data.name),
      description: sanitiseTextInput(data.description || ''),
      created_by: currentUser.uid,
      root_member: {
        first_name: sanitiseNameInput(data.root_first_name),
        last_name: sanitiseNameInput(data.root_last_name),
        date_of_birth: data.root_date_of_birth,
        gender: data.root_gender,
      },
      father: {
        first_name:
          sanitiseNameInput(data.father_first_name || '') ||
          `${sanitiseNameInput(data.root_first_name)}'s Father`,
        last_name: sanitiseNameInput(data.father_last_name || ''),
        date_of_birth: data.father_date_of_birth
          ? data.father_date_of_birth
          : null,
        gender: data.father_gender,
        is_alive: data.father_is_alive,
      },
      mother: {
        first_name:
          sanitiseNameInput(data.mother_first_name || '') ||
          `${sanitiseNameInput(data.root_first_name)}'s Mother`,
        last_name: sanitiseNameInput(data.mother_last_name || ''),
        date_of_birth: data.mother_date_of_birth
          ? data.mother_date_of_birth
          : null,
        gender: data.mother_gender,
        is_alive: data.mother_is_alive,
      },
    };
    setLoading(true);
    mutation.mutate(payload, {
      onSuccess: (response: ApiResponse<{ id: string }>) => {
        setLoading(false);
        enqueueSnackbar('Family tree added successfully', {
          variant: 'success',
        });
        const createdTreeId =
          'id' in response
            ? response.id
            : response.data && 'id' in response.data
              ? response.data.id
              : undefined;
        if (createdTreeId) {
          navigate(
            redirectToStory
              ? `/app/tree/${createdTreeId}?tab=stories&action=create`
              : `/app/tree/${createdTreeId}`,
          );
        }
        closeModal?.();
      },
      onError: (error) => {
        setLoading(false);
        console.error('Error creating family tree:', error);
        enqueueSnackbar(`Error creating family tree: ${error.message}`, {
          variant: 'error',
        });
      },
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        animation: 'treeFormFade 240ms cubic-bezier(0.2, 0, 0, 1)',
        '@keyframes treeFormFade': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: { xs: 3, md: 4 },
          pt: { xs: 3, md: 4 },
          pb: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h5" fontWeight={700}>
              {t('tree:createTree.title')}
            </Typography>
            <Chip label="Private" size="small" color="info" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Start with your details — you can add more family members later.
          </Typography>
        </Box>
        <IconButton onClick={closeModal} size="small" sx={{ mt: 0.5 }}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable content ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 3, md: 4 },
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* Tree info */}
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('tree:createTree.sections.treeName')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder="e.g. The Smith Family Tree"
              fullWidth
              required
              error={!!error}
              helperText={error?.message}
              inputProps={{ maxLength: textLimits.treeName }}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          rules={{ required: 'Description is required' }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('tree:createTree.sections.description')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder="e.g. A brief description of the family tree"
              multiline
              rows={2}
              fullWidth
              required
              error={
                !!error ||
                hasReachedCharacterLimit(
                  field.value,
                  textLimits.treeDescription,
                )
              }
              helperText={
                error?.message ??
                getCharacterCountHelperText(
                  field.value,
                  textLimits.treeDescription,
                )
              }
              inputProps={{ maxLength: textLimits.treeDescription }}
            />
          )}
        />

        {/* ── Root member card ── */}
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'primary.light',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('tree:createTree.sections.root.title')}
            </Typography>
            <Chip
              label="Required"
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Controller
              name="root_first_name"
              control={control}
              rules={{ required: 'First Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t('tree:createTree.sections.common.firstName')}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g. Taraji"
                  fullWidth
                  required
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{ maxLength: textLimits.personName }}
                />
              )}
            />
            <Controller
              name="root_last_name"
              control={control}
              rules={{ required: 'Last Name is required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t('tree:createTree.sections.common.lastName')}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g. Moyo"
                  fullWidth
                  required
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{ maxLength: textLimits.personName }}
                />
              )}
            />
          </Box>

          <Controller
            name="root_date_of_birth"
            control={control}
            rules={{ required: 'Date of birth is required' }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                minDate={dayjs('1850-01-01').toDate()}
                maxDate={dayjs().toDate()}
                label={t('tree:createTree.sections.common.dob')}
                format="dd/MM/yyyy"
                value={field.value ? new Date(field.value) : null}
                onChange={makeDateHandler(field.onChange)}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />

          <FormControl component="fieldset" required>
            <FormLabel>{t('tree:createTree.sections.common.gender')}</FormLabel>
            <Controller
              name="root_gender"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    label={t('common:male')}
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    label={t('common:female')}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Box>

        {/* ── Father accordion ── */}
        <Accordion
          disableGutters
          TransitionProps={{ unmountOnExit: false }}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('tree:createTree.sections.father.title')}
              </Typography>
              <Chip label="Optional" size="small" variant="outlined" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                pt: 0.5,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Controller
                  name="father_first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('tree:createTree.sections.common.firstName')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      placeholder="e.g. John"
                      inputProps={{ maxLength: textLimits.personName }}
                    />
                  )}
                />
                <Controller
                  name="father_last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('tree:createTree.sections.common.lastName')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      placeholder="e.g. Moyo"
                      inputProps={{ maxLength: textLimits.personName }}
                    />
                  )}
                />
              </Box>
              <Controller
                name="father_date_of_birth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    minDate={dayjs('1850-01-01').toDate()}
                    maxDate={dayjs().toDate()}
                    label={t('tree:createTree.sections.common.dob')}
                    format="dd/MM/yyyy"
                    value={field.value ? new Date(field.value) : null}
                    onChange={makeDateHandler(field.onChange)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel>
                    {t('tree:createTree.sections.common.gender')}
                  </FormLabel>
                  <Controller
                    name="father_gender"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="male"
                          control={<Radio />}
                          label={t('common:male')}
                        />
                        <FormControlLabel
                          value="female"
                          control={<Radio />}
                          label={t('common:female')}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                <Controller
                  name="father_is_alive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!field.value}
                          onChange={(_, checked) => field.onChange(!checked)}
                        />
                      }
                      label={t('tree:createTree.sections.common.deceased')}
                      labelPlacement="start"
                    />
                  )}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ── Mother accordion ── */}
        <Accordion
          disableGutters
          TransitionProps={{ unmountOnExit: false }}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t('tree:createTree.sections.mother.title')}
              </Typography>
              <Chip label="Optional" size="small" variant="outlined" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
                pt: 0.5,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Controller
                  name="mother_first_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('tree:createTree.sections.common.firstName')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      placeholder="e.g. Jane"
                      inputProps={{ maxLength: textLimits.personName }}
                    />
                  )}
                />
                <Controller
                  name="mother_last_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('tree:createTree.sections.common.lastName')}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      placeholder="e.g. Moyo"
                      inputProps={{ maxLength: textLimits.personName }}
                    />
                  )}
                />
              </Box>
              <Controller
                name="mother_date_of_birth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    minDate={dayjs('1850-01-01').toDate()}
                    maxDate={dayjs().toDate()}
                    label={t('tree:createTree.sections.common.dob')}
                    format="dd/MM/yyyy"
                    value={field.value ? new Date(field.value) : null}
                    onChange={makeDateHandler(field.onChange)}
                    slots={{ textField: TextField }}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                )}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <FormControl component="fieldset">
                  <FormLabel>
                    {t('tree:createTree.sections.common.gender')}
                  </FormLabel>
                  <Controller
                    name="mother_gender"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="male"
                          control={<Radio />}
                          label={t('common:male')}
                        />
                        <FormControlLabel
                          value="female"
                          control={<Radio />}
                          label={t('common:female')}
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
                <Controller
                  name="mother_is_alive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!field.value}
                          onChange={(_, checked) => field.onChange(!checked)}
                        />
                      }
                      label={t('tree:createTree.sections.common.deceased')}
                      labelPlacement="start"
                    />
                  )}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* ── Sticky footer ── */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          px: { xs: 3, md: 4 },
          py: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          type="button"
          variant="outlined"
          onClick={closeModal}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            minWidth: 120,
            transition:
              'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: 3 },
          }}
        >
          {t('tree:createTree.actionBtn')}
        </Button>
      </Box>
    </Box>
  );
}
